import React, { useContext, useState } from 'react';
import { ApiContext } from '@api/api';
import {
  AdminGetUsersFilter,
  AdminUserOutDTO,
  LovSecurityRolesDTO,
  PageResponseAdminUserOutDTO,
} from '@api/ringil-api.v2';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { NoWrapText, Row } from '@components/styles.tsx';
import { EditUserSidebar } from '@app/pages/customer/CompanyProfile/sidebar/EditUserSidebar.tsx';
import { FilterPage, useFilter } from '@hooks/useFilter/Filter.tsx';
import HorizontalFieldValue from '@components/obsolete/HorizontalFieldValue.tsx';

interface ComponentProps {
}

interface CompanyAndUserId {
  companyId: string;
  userId: string;
}

const AdminAllUsers: React.FC<ComponentProps> = () => {
  const { adminGetUsers, adminCreateUserInAuth0, adminGetSecurityRoles } = useContext(ApiContext);
  const [pageData, setPageData] = useState<PageResponseAdminUserOutDTO>();
  const { showToastMessage } = useContext(ToastContext);
  const [showPermisionForUser, setShowPermisionForUser] = useState<CompanyAndUserId>(undefined);

  const {
    Filter,
    applyFilterValue,
    filter,
    applyPaging,
    page,
    onPage,
    applySorting,
    sort,
  } = useFilter<AdminGetUsersFilter>({
    onLoad: (page: FilterPage, filter: AdminGetUsersFilter) => {
      load(page.pageSize, page.pageNumber, filter);
    },
    filterName: 'admin_all_users',
    defaultSort: { sortField: 'applicationId', sortDirection: 'DESC' },
  });

  const load = (pageSize: number, pageNumber: number, filter: AdminGetUsersFilter) => {
    adminGetUsers({ pageSize, pageNumber }, filter, d => {
      setPageData(d);
    });
  };

  if (isNullOrUndefined(pageData)) return <></>;

  const renderState = (row: AdminUserOutDTO) => {
    return (
      <>
        <div>
          {row.registered && <div>Registered</div>}
          {row.active && <div>Active</div>}
        </div>
      </>
    );
  };

  const renderActions = (row: AdminUserOutDTO) => {
    return (
      <NoWrapText>
        <Button
          label={'Create in Auth0'}
          onClick={e => {
            adminCreateUserInAuth0({ userId: row.id }, () => {
              showToastMessage('Success', 'User created in Auth0', 'success');
            });
          }}
          data-testid='admin-menuSecurity-users-actions-createInAuth0'
        />
        <Button
          label={'Manage permission'}
          onClick={e => {
            setShowPermisionForUser({
              companyId: row.companyId,
              userId: row.id,
            });
          }}
          data-testid='admin-menuSecurity-users-actions-managePermisions'
        />
        <a href={`/?runAs=${row.id}`} target={'_blank'} data-testid='admin-menuSecurity-users-actions-loginAsUser'>
          Login as user
        </a>
      </NoWrapText>
    );
  };

  return (
    <>
      <Row>
        <HorizontalFieldValue
          label={'email'}
          value={
            <InputText
              value={filter.email}
              onChange={e => {
                applyFilterValue('email', e.target.value);
              }}
              data-testid='admin-menuSecurity-users-filter-email'
            />
          }
        />
        <HorizontalFieldValue
          label={'firstName'}
          value={
            <InputText
              value={filter.firstName}
              onChange={e => {
                applyFilterValue('firstName', e.target.value);
              }}
              data-testid='admin-menuSecurity-users-filter-firstName'
            />
          }
        />
        <HorizontalFieldValue
          label={'lastName'}
          value={
            <InputText
              value={filter.lastName}
              onChange={e => {
                applyFilterValue('lastName', e.target.value);
              }}
              data-testid='admin-menuSecurity-users-filter-lastName'
            />
          }
        />
        <HorizontalFieldValue
          label={'companyName'}
          value={
            <InputText
              value={filter.companyName}
              onChange={e => {
                applyFilterValue('companyName', e.target.value);
              }}
              data-testid='admin-menuSecurity-users-filter-companyName'
            />
          }
        />
      </Row>
      {/*// @ts-ignore*/}
      <DataTable
        value={pageData.data}
        rows={pageData.size}
        paginator={true}
        lazy={true}
        totalRecords={pageData.total}
        onPage={onPage}
        first={(pageData.page - 1) * pageData.size}
      >
        <Column header={'email'} field={'email'} style={{ width: '25rem' }} />
        <Column header={'firstName'} field={'firstName'} style={{ width: '10rem' }} />
        <Column header={'lastName'} field={'lastName'} style={{ width: '10rem' }} />
        <Column header={'companyName'} field={'companyName'} style={{ width: '10rem' }} />
        <Column header={'state'} body={renderState} style={{ width: '10rem' }} />
        <Column header={'actions'} body={renderActions} style={{ width: '10rem' }} />
      </DataTable>
      {isNotNullOrUndefined(showPermisionForUser) && (
        <EditUserSidebar
          visible={isNotNullOrUndefined(showPermisionForUser)}
          onHide={() => {
            setShowPermisionForUser(undefined);
          }}
          onComplete={() => {
            setShowPermisionForUser(undefined);
            load(pageData.size, pageData.page, filter);
          }}
          userId={showPermisionForUser.userId}
          companyId={showPermisionForUser.companyId}
          loadSecurityRoles={(onSuccess: (data: LovSecurityRolesDTO[]) => void) =>
            adminGetSecurityRoles(data =>
              onSuccess(
                data.map(it => ({
                  id: it.id,
                  name: it.name,
                  translateKey: it.translateKey,
                  descriptionKey: it.descriptionKey,
                  isPrivate: it.isPrivate,
                  isOnlyInternal: it.isOnlyInternal,
                  companyModules: it.companyModules,
                }))
              )
            )
          }
          isAdmin={true}
        />
      )}
    </>
  );
};

export default AdminAllUsers;
